/**
 * @file Component to view the number of calls
 * @author Heino Lenting, Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { ChangeDetectorRef } from '@angular/core';
import { AlertsService } from '../../core/alerts.service';
import { EventsService } from '../../core/event.service';
import { LocalisationService } from '../../core/localisation.service';
import { ForecastService } from '../forecast.service';
import { PluginViewForecastBase, PROGNOSES, SPECIAL_DAYS, HOLIDAYS, CORRECTIONS } from '../plugin-view-forecast/plugin-view-forecast.base';
import { GridService } from '../../grids/grid.service';
import { SettingsService } from '../../settings.service';
/**
 * Class to show the number of calls.
 */
var PluginViewPrognosesComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PluginViewPrognosesComponent, _super);
    /**
     * Creates an instance of PluginCallsComponent.
     * @param {LocalisationService} localisationService The localisation service.
     * @param {ChangeDetectorRef} changeDetector The change detector is used to update the rendered layout.
     * @param {EventsService} eventsService The events service.
     * @param {SettingsService} settingsService The settings service.
     * @param {ForecastService} forecastService The forecast service is used to retrieve prognoses.
     * @param {AlertsService} alertsService The alerts service.
     * @memberOf PluginCallsComponent
     */
    function PluginViewPrognosesComponent(localisationService, changeDetector, eventsService, gridService, settingsService, forecastService, alertsService) {
        var _this = _super.call(this, localisationService, changeDetector, eventsService, gridService, settingsService, forecastService, alertsService) || this;
        /**
         * The names of the queries that are loaded when the plugin view refreshes.
         * @protected
         * @memberOf PluginViewCallsComponent
         */
        _this.queryNames = [PROGNOSES, SPECIAL_DAYS, HOLIDAYS, CORRECTIONS];
        _this.subscriptionPool.add(forecastService.processCompleted.subscribe(function () {
            _this.refresh();
        }));
        return _this;
    }
    /**
     * Builds the calls chart.
     * @protected
     * @memberOf PluginCallsComponent
     */
    PluginViewPrognosesComponent.prototype.build = function () {
        var _this = this;
        // The absolute highest value of all displayed charts.
        var maxValue = 0;
        var pluginBars = [];
        this.gridCollections.forEach(function (gridSegment) {
            gridSegment.children.forEach(function (gridRows) {
                gridRows.children.forEach(function (gridRow) {
                    // A grid column is one day on the planboard.
                    gridRow.children.forEach(function (gridColumn) {
                        // The special days and holidays are merely displayed.
                        var specialDay = _this.queryResults[SPECIAL_DAYS].find(function (s) { return gridColumn.meta.start.startOf('day').isSame(s['Date']); });
                        if (specialDay) {
                            gridColumn.specialDay = specialDay['Name'];
                        }
                        var holiday = _this.queryResults[HOLIDAYS].find(function (h) { return gridColumn.meta.start.isBetween(h['Start'], h['Finish'], 'day', '[]'); });
                        if (holiday) {
                            gridColumn.specialDay = holiday['Item']['Name'];
                        }
                        gridColumn.pluginBars = [];
                        if (_this.queryResults[PROGNOSES].length === 0) {
                            return;
                        }
                        // The correction factor is 1 if there is not correction.
                        var correctionEntity = _this.queryResults[CORRECTIONS].find(function (c) { return gridColumn.meta.start.startOf('day').isSame(c['Date']); });
                        var correction = correctionEntity ? correctionEntity['Value'] : 1;
                        var dimensions = _this.getBarDimensions(gridColumn);
                        var _loop_1 = function () {
                            // Collect all prognoses that fall within the dimensions.
                            // Prognoses may not overlap partially with a bar!
                            var prognoses = _this.queryResults[PROGNOSES].filter(function (p) { return dimensions.startTime.isSameOrBefore(p['Start']) && dimensions.endTime.isSameOrAfter(p['Finish']); });
                            if (prognoses.length > 0) {
                                // Sum the number of customers for the whole bar.
                                var value_1 = 0;
                                prognoses.forEach(function (prognosis) {
                                    value_1 += prognosis['Customers'];
                                });
                                if (value_1 > maxValue) {
                                    maxValue = value_1;
                                }
                                // The corrected number of customers is displayed in the bar.
                                var corrected = correction * value_1;
                                var pluginBar = {
                                    startHour: dimensions.startTime.hour(),
                                    startMinute: dimensions.startTime.minute(),
                                    endHour: dimensions.width.asDays() >= 1 ? 24 : dimensions.endTime.hour(),
                                    endMinute: dimensions.width.asDays() >= 1 ? 0 : dimensions.endTime.minute(),
                                    relativeValue: corrected,
                                    value: _this.toMaxTwoDecimals(corrected)
                                };
                                // If the correction factor is set, show the uncorrected estimated number of customers.
                                if (correction !== 1) {
                                    pluginBar.relativeValueExtra = value_1;
                                    pluginBar.valueExtra = _this.toMaxTwoDecimals(value_1);
                                    if (corrected > maxValue) {
                                        maxValue = corrected;
                                    }
                                }
                                // Add the bar to the plug-in.
                                gridColumn.pluginBars.push(pluginBar);
                                pluginBars.push(pluginBar);
                            }
                            // Move to the next bar.
                            dimensions.startTime = dimensions.endTime;
                            dimensions.endTime = dimensions.startTime.clone().add(dimensions.width);
                        };
                        do {
                            _loop_1();
                        } while (dimensions.endTime.isSameOrBefore(gridColumn.meta.finish));
                    });
                });
            });
        });
        // All charts need to be scaled equally, so the relative value can only be set at the end, when the maximum value is known.
        pluginBars.forEach(function (pluginBar) {
            pluginBar.relativeValue = Math.round(100 * pluginBar.relativeValue / maxValue);
            if (pluginBar.relativeValueExtra) {
                pluginBar.relativeValueExtra = Math.round(100 * pluginBar.relativeValueExtra / maxValue);
            }
        });
    };
    return PluginViewPrognosesComponent;
}(PluginViewForecastBase));
export { PluginViewPrognosesComponent };
